import {
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SelectorItem } from '@app/component/selector/selector.component';
import {
  Market,
  PaymentMethod,
  PaymentMethodVM,
} from '@app/model/payment-request/payment-request.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import {
  CheckoutStatus,
  StateService,
  AvailablePaymentMethods,
} from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { deInvoiceTermsUrl } from '@app/common/de-common';
import { TermsLinkService } from '@app/service/terms-link/terms-link.service';
import Translations from './user-flow-selection.component.json';
import { IframeService, WindowMode } from '@app/service/iframe/iframe.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatDialog } from '@angular/material/dialog';
import { CreditDecisionDialogComponent } from '@app/component/dialog/credit-decision/credit-decision-dialog.component';
import { LoaderMessagingService } from '@app/service/loader-messaging/loader-messaging.service';
import { Subscription } from 'rxjs';
import { ColorService } from '@app/service/color/color.service';
import { filter } from 'rxjs/operators';

interface Methodtranslations {
  title: string;
  subTitle: string;
  bulletPoints: string[];
}

interface UserFlowTranslationJson {
  errorNoAvailablePaymentsMethods: string;
  errorInvalidPRStateForProceeding: string;
  errorInfoText: string;
  continueButton: string;
  closeButton: string;
  links: { [key: string]: string };
  disclaimer: { [key: string]: string };
  paymentMethods: { [key: string]: Methodtranslations };
  purchaseInfo: { [key: string]: string };
  iframe: {
    title: string;
    reason: string;
    buttons: {
      open: string;
      close: string;
    };
  };
}

@Component({
  selector: 'user-flow-selection',
  templateUrl: './user-flow-selection.component.html',
  styleUrls: ['./user-flow-selection.component.scss', '../page-shared.css'],
})
export class UserFlowSelectionComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading: boolean = true;
  Market = Market;
  paymentMethodsVM: PaymentMethodVM[] = null;
  selectedPaymentMethodVM: PaymentMethodVM = null;
  navigationInProgress: boolean = false;
  selectorItems: SelectorItem[] = null;
  selectedItem: SelectorItem = null;
  modalActive: boolean = false;
  noAvailablePaymentMethods: boolean = false;
  invalidPRStateForProceeding: boolean = false;
  color: string = '#DB305E';
  token: string;
  isMobile: boolean;
  childWindow: Window;
  tabClosedSubscription: Subscription;
  isIframe: boolean;

  translations: UserFlowTranslationJson;

  constructor(
    protected router: Router,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected route: ActivatedRoute,
    private queryParamsService: QueryParamsService,
    private valuePipe: ValuePipe,
    private loggingService: LoggingService,
    private termsLinkService: TermsLinkService,
    private iframeService: IframeService,
    private deviceDetectorService: DeviceDetectorService,
    private dialogService: MatDialog,
    private loadermessagingService: LoaderMessagingService,
    private ngZone: NgZone,
    private colorService: ColorService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.stateService.getCurrentStateLocaleBaseCode(this.state?.language)
      ];

    if (
      this.state?.status === CheckoutStatus?.CANCELLED ||
      this.state?.status === CheckoutStatus?.EXPIRED
    ) {
      this.invalidPRStateForProceeding = true;
      return;
    }

    if (
      this.state?.forceNavigation &&
      this.state?.currentPageInformation?.pageName.toString() !== 'HOME'
    ) {
      // Let Route Component handle the force navigation...
      return; // This way we don't continue while the Route component is working.
    }

    this.token = this.queryParamsService.getQueryParams().id;
    localStorage.setItem('zco_market', this.state?.market);

    this.isIframe = this.iframeService.isThisInIframe();
    this.isMobile = this.deviceDetectorService.isMobile();
    this.color = this.colorService.getPrimaryColor();

    try {
      const paymentMethods: AvailablePaymentMethods[] =
        await this.stateService.getAvailablePaymentMethods(this.token);

      if (!paymentMethods || paymentMethods?.length === 0) {
        this.noAvailablePaymentMethods = true;
        return;
      }

      this.paymentMethodsVM = this.transformDtoToVm(paymentMethods);

      const getPaymentMethodByType = this.paymentMethodsVM.find(
        (method: PaymentMethodVM) =>
          method?.type === this.state?.paymentInformation?.paymentMethod
      );

      this.selectedPaymentMethodVM =
        getPaymentMethodByType || this.paymentMethodsVM[0];

      this.loading = false;
    } catch (err) {
      this.handleError(err);
    }
  }

  async ngOnDestroy(): Promise<void> {
    if (this.tabClosedSubscription) {
      this.tabClosedSubscription.unsubscribe();
    }
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: Event) {
    //('Clean up tab if iframe is closed or reloaded');
    if (this.isIframe) {
      this.loadermessagingService.closeChildWindow(this.childWindow);
    }
  }

  async handleContinueClick(): Promise<void> {
    if (this.navigationInProgress) {
      return;
    }

    this.loggingService.warn(
      'selected payment method and navigating: ' +
        this.selectedPaymentMethodVM.type
    );

    this.navigationInProgress = true;
    let windowMode: string = this.iframeService.getWindowMode(this.token);
    let nextRoute: string;

    try {
      nextRoute = await this.stateService
        .registerChosenMethod(this.selectedPaymentMethodVM.id)
        .then((checkoutState) => {
          return this.stateService.getRouteFromState(checkoutState);
        });
    } catch (error) {
      this.loggingService.warn(
        `Error registering chosen payment method, error: ${error}`
      );
      this.navigate();
    }

    if (this.isIframe && windowMode === WindowMode.POPUP) {
      this.createTabClosedSubscription();

      this.childWindow = this.iframeService.openZaverPopup(
        `/${nextRoute}?id=${this.token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker();
    } else if (
      (this.isIframe && windowMode === WindowMode.TAB) ||
      (this.isIframe &&
        this.isMobile &&
        !this.iframeService.isThisAChildWindow())
    ) {
      this.createTabClosedSubscription();

      this.childWindow = this.iframeService.openZaverTab(
        `/${nextRoute}?id=${this.token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker();
    } else {
      this.router.navigate([nextRoute], { queryParamsHandling: 'preserve' });
    }

    this.navigationInProgress = false;
  }

  deactivateModal(): void {
    this.modalActive = false;
  }

  getSekkiLink(): string {
    switch (this.state.market) {
      case Market.SE:
        //TODO: BACKEND DRIVEN SE DOESN'T SEND FRINK CREDITOR SEKKI
        return this.termsLinkService.SEKKI_PDF_LINK;
      case Market.AT:
      case Market.DE:
        return 'https://zaver.com/terms-de/impressum';
      case Market.FI:
        return this.state.checkoutCustomizations.merchantTermsUrl;
      default:
        break;
    }
  }

  getPartEightDisclaimer(): string {
    return this.translations.disclaimer.partEight.replace(
      '${MERCHANT}',
      this.state.checkoutCustomizations.merchantName
    );
  }

  getModalPurchaseInfo(): void {
    if (this.state.market !== Market.SE) {
      this.selectorItems = [
        {
          tab: this.translations.purchaseInfo.tab,
          title: this.translations.purchaseInfo.title,
          lineItems: this.state.paymentInformation?.lineItems,
          totalValue: this.stateService.getCustomTaxTypeText(
            `${
              this.translations.purchaseInfo.totalValue
            } ${this.valuePipe.transform(this.state.totalCheckoutValue)}`,
            this.state.checkoutCustomizations
          ),
        },
      ];
    }
    this.selectedItem = this.selectorItems[0];
    this.modalActive = true;
  }

  openModalWithTerms(): void {
    this.getModalPurchaseInfo();
    if (
      this.selectedPaymentMethodVM.type === PaymentMethod.INVOICE ||
      this.selectedPaymentMethodVM.type === PaymentMethod.INVOICE_MCO
    ) {
      this.selectorItems = [
        ...this.selectorItems,
        {
          tab: 'Zahlungsbedingungen',
          title: 'Bedingungen für den Kauf auf Rechnung',
          descriptions: [
            {
              text:
                'Wenn Sie sich für die Zahlung per Rechnung entscheiden, bieten wir Ihnen die Möglichkeit, innerhalb von ' +
                this.selectedPaymentMethodVM.promotionalDurationDays +
                ' Tagen ab dem Datum der Rechnungsstellung zu zahlen.',
            },
          ],
          links: [
            {
              text: 'Allgemeine Zahlungsbedingungen',
              onClick: () => this.openURLinNewWindow(deInvoiceTermsUrl),
            },

            {
              ...(this.state.checkoutCustomizations.merchantTermsUrl !== null &&
                this.state.checkoutCustomizations.merchantName !== null && {
                  text: this.stateService.getCustomTermsUrlText(
                    this.state.checkoutCustomizations.merchantName + ' AGB',
                    this.state.checkoutCustomizations
                  ),
                  onClick: () =>
                    this.openURLinNewWindow(
                      this.state.checkoutCustomizations.merchantTermsUrl
                    ),
                }),
            },
            {
              ...(this.state.market === Market.AT && {
                text: 'Europäische Standardinformationen für Kreditierungen nach dem Verbraucherkreditgesetz',
                onClick: () =>
                  this.openURLinNewWindow(
                    this.state.checkoutCustomizations.merchantTermsUrl
                  ),
              }),
            },
          ],
        },
      ];
    }

    this.selectedItem = this.selectorItems[this.selectorItems.length - 1];
    this.modalActive = true;
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  onTermsClick(): void {
    if (
      this.selectedPaymentMethodVM.type === 'INVOICE' ||
      this.selectedPaymentMethodVM.type === 'INVOICE_MCO' ||
      this.selectedPaymentMethodVM.type === 'TOKENIZED_CREDIT_LIMIT'
    ) {
      this.openModalWithTerms();
    }
  }

  selectPaymentMethod(method: PaymentMethodVM): void {
    this.selectedPaymentMethodVM = method;
  }

  transformDtoToVm(
    paymentMethods: AvailablePaymentMethods[]
  ): PaymentMethodVM[] {
    return paymentMethods.map((method) => {
      switch (method.type) {
        case PaymentMethod.INVOICE:
        case PaymentMethod.INVOICE_MCO:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'invoice', size: 26 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
            promotionalDurationDays: method.promotionalDurationDays,
          };
        case PaymentMethod.CREDIT:
        case PaymentMethod.CREDIT_MCO:
        case PaymentMethod.CREDIT_STATIC_AMOUNT:
        case PaymentMethod.CREDIT_DIRECT_DEBIT:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'cake', size: 26 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
          };
        case PaymentMethod.TRUSTLY:
        case PaymentMethod.FINAPI_A2A:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'bank', size: 26 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
          };
        case PaymentMethod.VIPPS_EXTERNAL:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'vippsSymbol', size: 20 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
          };
        case PaymentMethod.COLLECTIVE_INVOICE:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'calendar', size: 26 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
          };
        default:
          return null;
      }
    });
  }

  getMethodtranslations(method: AvailablePaymentMethods): Methodtranslations {
    switch (method.type) {
      case PaymentMethod.TRUSTLY:
      case PaymentMethod.FINAPI_A2A:
        return {
          title: this.translations.paymentMethods.directTransfer.title,
          subTitle: this.translations.paymentMethods.directTransfer.subTitle,
          bulletPoints:
            this.translations.paymentMethods.directTransfer.bulletPoints,
        };
      case PaymentMethod.INVOICE:
      case PaymentMethod.INVOICE_MCO:
        const invoiceDuration = (method: AvailablePaymentMethods) =>
          method.promotionalDurationDays.toLocaleString(
            this.state?.language ?? 'en-US',
            {
              maximumFractionDigits: 0,
            }
          );
        return {
          title: this.translations.paymentMethods.invoice.title,
          subTitle: this.translations.paymentMethods.invoice.subTitle.replace(
            '${DURATION}',
            invoiceDuration(method)
          ),
          bulletPoints:
            this.translations.paymentMethods.invoice.bulletPoints.map(
              (bulletPoint) =>
                bulletPoint.replace('${DURATION}', invoiceDuration(method))
            ),
        };
      case PaymentMethod.COLLECTIVE_INVOICE:
        return {
          title: this.translations.paymentMethods.collectiveInvoice.title,
          subTitle: this.translations.paymentMethods.collectiveInvoice.subTitle,
          bulletPoints:
            this.translations.paymentMethods.collectiveInvoice.bulletPoints,
        };
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_MCO:
      case PaymentMethod.CREDIT_DIRECT_DEBIT:
      case PaymentMethod.CREDIT_STATIC_AMOUNT:
        const lowestPossibleMonthlyPayment = (
          method: AvailablePaymentMethods
        ) =>
          method.lowestPossibleMonthlyPayment.toLocaleString(
            this.state?.language ?? 'en-US',
            {
              maximumFractionDigits: 0,
            }
          );

        const creditTitle =
          PaymentMethod.CREDIT_STATIC_AMOUNT === method.type
            ? this.translations.paymentMethods.creditStatic.title
            : this.translations.paymentMethods.credit.title;
        return {
          title: creditTitle,
          subTitle: this.translations.paymentMethods.credit.subTitle.replace(
            '${LOWESTPAYMENT}',
            lowestPossibleMonthlyPayment(method)
          ),
          bulletPoints: this.translations.paymentMethods.credit.bulletPoints,
        };
      case PaymentMethod.VIPPS_EXTERNAL:
        return {
          title: this.translations.paymentMethods.vippsExternal.title,
          subTitle: this.translations.paymentMethods.vippsExternal.subTitle,
          bulletPoints:
            this.translations.paymentMethods.vippsExternal.bulletPoints,
        };
    }
  }

  handleCloseClick(): void {
    this.loadermessagingService.closeChildWindow(this.childWindow);
    this.reset();
    this.navigationInProgress = false;
  }

  createButtons() {
    let buttons = [];
    //You can't focus on new tab with mobile.
    if (!this.isMobile) {
      buttons.push({
        title: this.translations.iframe.buttons.open,
        callback: () => {
          this.childWindow.focus();
          this.createNewWindowBlocker();
        },
      });
    }
    buttons.push({
      title: this.translations.iframe.buttons.close,
      callback: () => {
        this.handleCloseClick();
      },
    });

    return buttons;
  }

  createNewWindowBlocker(): void {
    const dialogConfiguration = {
      maxHeight: '75vh',
      maxWidth: '90vw',
      data: {
        title: this.translations.iframe.title,
        reason: this.translations.iframe.reason,
        buttons: this.createButtons(),
      },
    };
    this.dialogService.open(CreditDecisionDialogComponent, dialogConfiguration);
  }

  createTabClosedSubscription(): void {
    if (!this.tabClosedSubscription) {
      this.tabClosedSubscription = this.loadermessagingService.event$
        .pipe(filter((message) => message === 'closed')) // Loader Messaging Service sent message 'closed'
        .subscribe(() => {
          this.ngZone.run(() => {
            this.childWindow = null;
          });
        });
    }
  }
}
